import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import CreateOrganization from '../components/admin/CreateOrganization';
import OrganizationDetails from '../components/admin/OrganizationDetails';
import { FaPlus, FaBuilding } from 'react-icons/fa';

export default function OrganizationManagementPage() {
  const navigate = useNavigate();
  const { organizations, loading, isMember, orgId, refreshOrganizationData, setOrganizationContext, currentContext } = useOrganization();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    // Always allow creating new organizations since users can be in multiple
    // Only hide form if user is currently viewing an organization
    if (isMember && orgId && !showCreateForm) {
      setShowCreateForm(false);
    }
  }, [isMember, orgId, showCreateForm]);

  const handleOrganizationCreated = async () => {
    setShowCreateForm(false);
    setRefreshTrigger(prev => prev + 1);
    // Refresh organization data
    await refreshOrganizationData();
  };

  const handleOrganizationUpdated = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleLeaveOrganization = async () => {
    // Refresh organization data
    await refreshOrganizationData();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Organization Management</h1>
              <p className="mt-2 text-gray-600">
                Create and manage your organizations
              </p>
            </div>
            
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FaPlus className="h-4 w-4 mr-2" />
              Create New Organization
            </button>
          </div>
        </div>

        {!isMember && !showCreateForm && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FaBuilding className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No Organizations Found
            </h2>
            <p className="text-gray-600 mb-6">
              You're not currently part of any organizations. Create your own organization to get started.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center mx-auto px-6 py-3 text-base font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FaPlus className="h-4 w-4 mr-2" />
              Create Your Organization
            </button>
          </div>
        )}

        {showCreateForm && (
          <div className="mb-8">
            <CreateOrganization
              onOrganizationCreated={handleOrganizationCreated}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        )}

        {isMember && currentContext.organization_id ? (
          <div>
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">
                    Active Organization: {currentContext.organization_name || 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
            <OrganizationDetails
              organizationId={currentContext.organization_id}
              onOrganizationUpdated={handleOrganizationUpdated}
              onLeaveOrganization={handleLeaveOrganization}
            />
          </div>
        ) : isMember && organizations.length > 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <FaBuilding className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-lg font-medium text-yellow-800 mb-2">
              No Active Organization Selected
            </h3>
            <p className="text-yellow-700 mb-4">
              You have organizations but none are currently active. Select an organization below to view and manage it.
            </p>
          </div>
        ) : null}

        {organizations.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Organizations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {organizations.map((org) => {
                const isActive = org.id === currentContext.organization_id;
                return (
                  <div key={org.id} className={`bg-white rounded-lg shadow-md p-6 border-2 ${isActive ? 'border-green-500' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <h3 className="text-lg font-medium text-gray-900">{org.name}</h3>
                        {isActive && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        )}
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 capitalize">
                        {org.role}
                      </span>
                    </div>
                    {isActive ? (
                      <div className="text-sm text-gray-500 text-center py-2">
                        Currently active
                      </div>
                    ) : (
                      <button
                        onClick={async () => {
                          await setOrganizationContext(org.id);
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Switch to this organization
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 